import { motion } from "framer-motion";
import { Upload, MessageSquare, Trophy, Briefcase, ArrowRight } from "lucide-react";

const steps = [
  {
    icon: Upload,
    title: "Day 1-3: Connect & Configure",
    description: "Post your job, set screening criteria, and connect your calendars. The AI starts learning your requirements immediately.",
    accent: "bg-accent/10 text-accent border-accent/20",
    features: ["Job posting in 2 clicks", "AI criteria calibration", "Calendar & ATS integration"],
  },
  {
    icon: MessageSquare,
    title: "Week 1-2: AI Screens Autonomously",
    description: "The Voice AI and Resume Parser begin screening candidates 24/7. No manual effort needed - candidates are scored and ranked automatically.",
    accent: "bg-blue-500/10 text-blue-500 border-blue-500/20",
    features: ["24/7 voice screening", "Auto-scoring & ranking", "Drop-off reduction"],
  },
  {
    icon: Briefcase,
    title: "Week 3-4: Pipeline Accelerates",
    description: "Automation rules kick in - top candidates are auto-advanced, interviews are self-scheduled, and your team reviews only the best.",
    accent: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
    features: ["Auto-advance top talent", "Self-scheduled interviews", "Team collaboration"],
  },
  {
    icon: Trophy,
    title: "Month 2+: Measurable ROI",
    description: "Track hiring velocity, cost savings, and quality improvements through real-time analytics. Continuously optimize your pipeline.",
    accent: "bg-amber-500/10 text-amber-500 border-amber-500/20",
    features: ["Time-to-hire analytics", "Cost-per-hire tracking", "Quality of hire metrics"],
  },
];

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.2 } },
};

const item = {
  hidden: { opacity: 0, x: -20 },
  show: { opacity: 1, x: 0, transition: { duration: 0.5 } },
};

const HowItWorksSection = () => {
  return (
    <section className="py-32 bg-white relative overflow-hidden">
      {/* Removed vertical line */}

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-50 text-slate-500 text-[10px] font-bold uppercase tracking-widest mb-4 border border-slate-200"
          >
            Implementation
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-display text-4xl md:text-6xl font-bold text-slate-900 mb-6"
          >
            From Manual to <span className="text-accent italic">Autonomous</span> <br /> in 4 Steps
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg text-slate-500 max-w-xl mx-auto font-medium"
          >
            Go from chaos to a fully autonomous AI-powered hiring pipeline in weeks, not months.
          </motion.p>
        </div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="grid md:grid-cols-4 gap-6 relative"
        >
          {steps.map((step, index) => (
            <motion.div
              key={index}
              variants={item}
              className="relative flex flex-col items-center group"
            >
              {/* Desktop arrow connectors */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-12 left-[70%] w-[60%] z-0 pointer-events-none">
                  <motion.div
                    initial={{ width: 0, opacity: 0 }}
                    whileInView={{ width: '100%', opacity: 1 }}
                    transition={{ delay: index * 0.2 + 0.5, duration: 0.8 }}
                    className="h-px bg-gradient-to-r from-slate-200 to-transparent"
                  />
                </div>
              )}

              {/* Step indicator */}
              <div className={`w-24 h-24 rounded-3xl bg-white border border-slate-100 shadow-sm flex items-center justify-center mb-8 relative z-10 group-hover:shadow-xl group-hover:scale-105 transition-all duration-500`}>
                <step.icon className={`w-10 h-10 ${step.accent.split(' ')[1]}`} />
                <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-slate-900 border-2 border-white flex items-center justify-center text-xs font-bold text-white shadow-lg">
                  {index + 1}
                </div>
              </div>

              {/* Content */}
              <div className="text-center px-2">
                <h3 className="font-display font-bold text-base text-slate-900 mb-3">{step.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed font-medium mb-4">
                  {step.description}
                </p>
                <ul className="space-y-1.5">
                  {step.features.map((f) => (
                    <li key={f} className="text-xs text-slate-400 font-medium flex items-center justify-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-accent flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Mobile arrow */}
              {index < steps.length - 1 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  className="md:hidden mt-8 mb-4 rotate-90"
                >
                  <ArrowRight className="w-6 h-6 text-slate-200" />
                </motion.div>
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
